/**
 * CartPriceManager - Utility class for formatting and updating cart prices
 * 
 * Integrates with CartSelectors to update price displays throughout the cart.
 * Uses Shopify.formatMoney for consistent price formatting.
 * 
 * Usage:
 *   const formatter = new CartPriceManager();
 *   formatter.updateCartTotal(); // Updates cart total
 *   formatter.updateShippingTotal(); // Updates shipping
 *   formatter.updateRecycleTotal(); // Updates recycle fee
 *   const formatted = formatter.formatMoney(99.99); // Returns formatted string
 */
class CartPriceManager {
  /**
   * Constructor
   * @param {Object} options - Optional configuration
   * @param {string} options.moneyFormat - Shopify money format (defaults to theme.moneyFormat)
   */
  constructor(options = {}) {
    this.moneyFormat = options.moneyFormat || (typeof theme !== 'undefined' ? theme.moneyFormat : window.theme?.moneyFormat);

    if (!this.moneyFormat) {
      console.warn('CartPriceManager: moneyFormat not found, using default');
      this.moneyFormat = '{{amount}}';
    }
  }

  /**
   * lightweight analog for getTotalShippingCost_shipp_method
   * calculate totalShipping and sync shipping radios (uncheck for items without _delivery_option)
   * @returns Promise<number>
   */
  static async getTotalShippingCost() {
    const curacaoCost = [];
    const sekoCost = [];

    const cart = await window.cartService.read();

    let shippingPrice = cart.items.reduce((acc, item, idx) => {
      const properties = item.properties || {}
      const { _delivery_charge, _delivery_option } = properties

      const price = parseFloat(_delivery_charge || 0)

      switch (_delivery_option) {
        case CartConstants.WHITE_GLOVE_IN_HOME_DELIVERY_SHIPPING_METHOD_NAME: {
          curacaoCost.push(price);
          console.log(`[CartPriceManager] getTotalShippingCost: item[${idx}] id=${item.id} sku=${item.sku} → curacao (White Glove) $${price}`);
          break
        }
        case CartConstants.IN_HOME_DELIVERY_SHIPPING_METHOD_NAME: {
          sekoCost.push(price);
          console.log(`[CartPriceManager] getTotalShippingCost: item[${idx}] id=${item.id} sku=${item.sku} → seko (In-Home) $${price}`);
          break
        }
        default: {
          acc += price;
          const optionLabel = _delivery_option || (_delivery_charge ? 'pickup/other' : 'none');
          console.log(`[CartPriceManager] getTotalShippingCost: item[${idx}] id=${item.id} sku=${item.sku} → default (${optionLabel}) $${price}, runningTotal=$${acc}`);
          break;
        }
      }

      return acc
    }, 0)

    console.log('[CartPriceManager] getTotalShippingCost: after reduce', { baseTotal: shippingPrice, curacaoCost, sekoCost });

    if (curacaoCost.length > 0) {
      const curacaoMax = Math.max(...curacaoCost);
      shippingPrice += curacaoMax;
      console.log(`[CartPriceManager] getTotalShippingCost: +curacao max(${curacaoCost.join(', ')}) = $${curacaoMax}, total=$${shippingPrice}`);
    }
    if (sekoCost.length > 0) {
      const sekoMax = Math.max(...sekoCost);
      shippingPrice += sekoMax;
      console.log(`[CartPriceManager] getTotalShippingCost: +seko max(${sekoCost.join(', ')}) = $${sekoMax}, total=$${shippingPrice}`);
    }

    console.log('[CartPriceManager] getTotalShippingCost: result', shippingPrice);
    return shippingPrice
  }

  static async getCartSubTotal() {
    const cart = await window.cartService.read()

    return (cart?.items_subtotal_price || 0) / 100
  }

  static async recycle_total() {
    const cart = await window.cartService.read()

    return cart.items.reduce((acc, item) => {
      const feePerItem = item.properties._recycle_fee || 0;

      acc += feePerItem * item.quantity;

      return acc
    }, 0)
  }

  /**
   * Format amount as money string
   * @param {number} amount - Amount in dollars (e.g., 125.50)
   * @returns {string} Formatted money string (e.g., "$125.50")
   */
  formatMoney(amount) {
    if (typeof amount !== 'number' || isNaN(amount)) {
      console.warn('CartPriceManager.formatMoney: Invalid amount', amount);
      return '';
    }

    // Convert dollars to cents (Shopify expects cents)
    const amountInCents = amount * 100;

    // Use Shopify.formatMoney if available, otherwise fallback
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(amountInCents, this.moneyFormat);
    }

    // Fallback formatting
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  async updateCartTotal() {
    const cartMoneyElement = document.querySelector(CartSelectors?.cart?.totals?.cartMoney());
    if (!cartMoneyElement) {
      console.warn('CartPriceManager.updateCartTotal: Cart money element not found');
      return
    }

    const totalShipping = await CartPriceManager.getTotalShippingCost()
    const cartSubTotal = await CartPriceManager.getCartSubTotal()

    const total = totalShipping + cartSubTotal;

    if (typeof total !== 'number' || isNaN(total)) {
      console.warn('CartPriceManager.updateCartTotal: Invalid total', total);
      return;
    }

    cartMoneyElement.innerHTML = this.formatMoney(total);
  }

  async updateSubTotal() {
    const subTotalNode = document.querySelector(CartSelectors.cart.totals.subTotal());
    if (!subTotalNode) {
      console.warn('CartPriceManager.updateSubTotal: Subtotal element not found');
      return;
    }

    const cartSubTotal = await CartPriceManager.getCartSubTotal();

    if (typeof cartSubTotal !== 'number' || isNaN(cartSubTotal)) {
      console.warn('CartPriceManager.updateSubTotal: Invalid subtotal', cartSubTotal);
      return;
    }

    subTotalNode.innerHTML = this.formatMoney(cartSubTotal)
  }

  async updateShippingTotal() {
    const shippingElement = document.querySelector(CartSelectors?.cart?.totals?.shipping());
    if (!shippingElement) {
      console.warn('CartPriceManager.updateShippingTotal: Shipping element not found');
      return
    }

    const total = await CartPriceManager.getTotalShippingCost()
    if (typeof total !== 'number' || isNaN(total)) {
      console.warn('CartPriceManager.updateShippingTotal: Invalid total', total);
      return;
    }

    shippingElement.innerHTML = this.formatMoney(total);

  }

  async updateRecycleTotal() {
    const total = parseFloat(await CartPriceManager.recycle_total());
    if (typeof total !== 'number' || isNaN(total)) {
      console.warn('CartPriceManager.updateRecycleTotal: Invalid total', total);
      return;
    }

    const formatted = this.formatMoney(total);
    const recycleElement = document.querySelector(
      CartSelectors?.cart?.totals?.recycle() || '.info-checkout.info-recycle-total .recycle_total'
    );

    if (recycleElement) {
      recycleElement.innerHTML = formatted;
    } else {
      console.warn('CartPriceManager.updateRecycleTotal: Recycle element not found');
    }

    const recycleSection = document.querySelector('.info-checkout.info-recycle-total');

    if (recycleSection) {
      if (total > 0) {
        recycleSection.classList.remove('hidden');
      } else {
        recycleSection.classList.add('hidden');
      }
    }
  }

  async updateTotalsSidebar() {
    const promises = []

    promises.push(this.updateCartTotal())
    promises.push(this.updateSubTotal())
    promises.push(this.updateShippingTotal())
    promises.push(this.updateRecycleTotal())

    await Promise.allSettled(promises)
  }
}

// Export for use in modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartPriceManager;
}

// Make available globally
window.CartPriceManager = CartPriceManager;
